import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useCallback, useContext } from 'react';
import { buildLoginRequest, buildApiScopes } from './msalConfig';
import { clearAnonSession, clearPersistedConversationId, closeBrowserLiveOwnership } from '../api/chatClient';
import { RuntimeConfigContext } from '../config/runtimeConfigContext';

const inFlightApiTokens = new Map<string, Promise<string>>();

export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const cfg = useContext(RuntimeConfigContext);
  const account = accounts[0];

  // The authoritative MSA PUID is the `puid` claim on the id_token — NOT derivable from the
  // homeAccountId/oid. MSAL persists id_token claims on the cached account entity, so this is
  // populated both right after an interactive sign-in AND on a silent/cached restart where the
  // user is already signed in (accounts[0] is rehydrated from the MSAL cache). tid is the tenant.
  const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
  const puid = typeof claims?.puid === 'string' ? (claims.puid as string) : undefined;
  const tenantId =
    account?.tenantId ||
    (typeof claims?.tid === 'string' ? (claims.tid as string) : undefined);

  const signIn = useCallback(async () => {
    if (!cfg) return;
    await closeBrowserLiveOwnership();
    await instance.loginRedirect(buildLoginRequest(cfg));
  }, [instance, cfg]);

  const signOut = useCallback(() => {
    // Drop the persisted anonymous chat session so the post-logout anonymous user doesn't
    // resume the signed-in user's server-side conversation.
    // Also drop the persisted active conversation id for the same reason — the next (anonymous)
    // user must not resume this user's conversation on refresh.
    // Pass the explicit account so MSAL clears that account's cache entries deterministically
    // (rather than relying on an implicit active account).
    void closeBrowserLiveOwnership()
      .catch(error => console.warn('Live ownership teardown failed', error))
      .finally(() => {
        clearAnonSession();
        clearPersistedConversationId();
        void instance.logoutRedirect({ account: account ?? undefined });
      });
  }, [instance, account]);

  const acquireApiToken = useCallback(async (): Promise<string | null> => {
    // Only signed-in users get a user-delegated token.
    // Anonymous users from the built-in SPA don't need a bearer — the middleware
    // recognises same-origin requests and skips the bearer check.
    if (!account || !cfg) return null;

    const scopes = buildApiScopes(cfg);
    const requestKey = `${account.homeAccountId}|${scopes.join(' ')}`;
    const inFlight = inFlightApiTokens.get(requestKey);
    if (inFlight) {
      return inFlight;
    }

    const acquisition = (async () => {
      try {
        const result = await instance.acquireTokenSilent({ account, scopes });
        return result.accessToken;
      } catch {
        const result = await instance.acquireTokenPopup({ account, scopes });
        return result.accessToken;
      }
    })();
    inFlightApiTokens.set(requestKey, acquisition);

    try {
      return await acquisition;
    } finally {
      if (inFlightApiTokens.get(requestKey) === acquisition) {
        inFlightApiTokens.delete(requestKey);
      }
    }
  }, [account, instance, cfg]);

  return {
    account,
    isSignedIn: Boolean(account),
    /**
     * True while MSAL is still starting up — handling a redirect response or rehydrating a cached
     * account. `accounts` is empty during that window, so `isSignedIn` reads false for a user who
     * IS signed in.
     *
     * Callers that stamp identity onto durable server state must wait for this to clear. The chat
     * session's identity is cached once per conversation, so an init that lands here brands the
     * whole conversation anonymous — and it stays anonymous even after the UI renders the signed-in
     * user. Downstream that surfaces as a consumer support case created with no PUID, which MSaaS
     * rejects with an opaque HTTP 400.
     */
    isAuthPending: inProgress !== InteractionStatus.None,
    puid,
    tenantId,
    signIn,
    signOut,
    acquireApiToken,
  };
}
